const DESIGNER_DRAFT_VERSION = 1;
const DRAFT_KEY_PREFIX = "mcb:designer:draft:v1";

export const getDesignerDraftVersion = () => DESIGNER_DRAFT_VERSION;

export const getDesignerDraftKey = ({ orderItemId, bSide, productModelId = null }) =>
    `${DRAFT_KEY_PREFIX}:${orderItemId || "unknown"}:${Number(bSide) === 1 ? 1 : 0}:${productModelId || "default"}`;

export const serializeDesignerDraft = ({
    orderItemId,
    bSide,
    state,
    currentStep,
    stripeDrawingsBySide = null,
    customCutsBySide = null,
    customCutZoneColors = null,
    customCutEditor = null,
    activePartSectionKey = "",
}) => {
    if (!state || !orderItemId) return null;
    return {
        meta: {
            version: DESIGNER_DRAFT_VERSION,
            savedAt: new Date().toISOString(),
            orderItemId: String(orderItemId),
            b: Number(bSide) === 1 ? 1 : 0,
        },
        payload: {
            currentStep: Number(currentStep) || 1,
            activePartSectionKey: String(activePartSectionKey || ""),
            state: {
                selectedKeys: Array.from(state.selectedKeys || []),
                selectedMaterials: state.selectedMaterials || {},
                selectedColors: state.selectedColors || {},
                selectedPrints: state.selectedPrints || {},
                selectedFullprints: state.selectedFullprints || {},
                partSublimation: state.partSublimation || {},
                printFilters: state.printFilters || {},
                generalMaterialId: state.generalMaterialId || null,
                generalSublimation: !!state.generalSublimation,
                generalFullprint: state.generalFullprint || null,
                activePartKey: state.activePartKey || null,
                activePrintKey: state.activePrintKey || null,
                viewSide: state.viewSide === "back" ? "back" : "front",
            },
            stripe_drawings: {
                front: Array.isArray(stripeDrawingsBySide?.front) ? stripeDrawingsBySide.front : [],
                back: Array.isArray(stripeDrawingsBySide?.back) ? stripeDrawingsBySide.back : [],
            },
            custom_cuts: {
                front: Array.isArray(customCutsBySide?.front) ? customCutsBySide.front : [],
                back: Array.isArray(customCutsBySide?.back) ? customCutsBySide.back : [],
                zone_colors: customCutZoneColors && typeof customCutZoneColors === "object" ? customCutZoneColors : {},
                editor: customCutEditor && typeof customCutEditor === "object" ? customCutEditor : {},
            },
        },
    };
};

const sanitizeObjectByKnownKeys = (obj, knownSet) => {
    const input = obj || {};
    const result = {};
    Object.keys(input).forEach((key) => {
        if (knownSet.has(String(key))) {
            result[String(key)] = input[key];
        }
    });
    return result;
};

export const parseDesignerDraft = ({ raw, orderItemId, bSide, knownPartKeys }) => {
    if (!raw) return { ok: false, reason: "empty" };
    try {
        const parsed = JSON.parse(raw);
        const meta = parsed?.meta || {};
        const payload = parsed?.payload || {};
        if (Number(meta.version) !== DESIGNER_DRAFT_VERSION) return { ok: false, reason: "version" };
        if (String(meta.orderItemId) !== String(orderItemId)) return { ok: false, reason: "order_item" };
        if (Number(meta.b || 0) !== (Number(bSide) === 1 ? 1 : 0)) return { ok: false, reason: "side" };

        const knownSet = new Set((knownPartKeys || []).map(String));
        const inputState = payload?.state || {};
        const selectedKeys = (inputState.selectedKeys || []).map(String).filter((key) => key === "master" || knownSet.has(key));
        const selectedMaterials = sanitizeObjectByKnownKeys(inputState.selectedMaterials, knownSet);
        const selectedPrints = {};
        Object.entries(inputState.selectedPrints || {}).forEach(([printKey, selection]) => {
            const locationKey = String(selection?.attrs?.location_key || printKey).split("::")[0];
            if (!knownSet.has(locationKey)) return;
            selectedPrints[String(printKey)] = selection;
        });
        const selectedFullprints = sanitizeObjectByKnownKeys(inputState.selectedFullprints, knownSet);
        const partSublimation = sanitizeObjectByKnownKeys(inputState.partSublimation, knownSet);
        const printFilters = sanitizeObjectByKnownKeys(inputState.printFilters, knownSet);

        const selectedColors = {};
        Object.entries(inputState.selectedColors || {}).forEach(([key, value]) => {
            if (String(key).includes("-print-color")) {
                const printKey = String(key).split("-print-color")[0];
                if (!selectedPrints[printKey]) return;
                selectedColors[String(key)] = value;
                return;
            }
            const partKey = String(key).split("-")[0];
            if (!partKey) return;
            const hasPrintForPart = Object.entries(selectedPrints).some(([printKey, selection]) => {
                const locationKey = String(selection?.attrs?.location_key || printKey).split("::")[0];
                return locationKey === partKey;
            });
            if (!knownSet.has(partKey) && !hasPrintForPart) return;
            selectedColors[String(key)] = value;
        });

        const rawStripeDrawings = payload?.stripe_drawings || {};
        const hasStripeDrawings = Boolean(payload && Object.prototype.hasOwnProperty.call(payload, "stripe_drawings"));
        const stripeDrawingsBySide = {
            front: Array.isArray(rawStripeDrawings?.front) ? rawStripeDrawings.front : [],
            back: Array.isArray(rawStripeDrawings?.back) ? rawStripeDrawings.back : [],
        };
        const rawCustomCuts = payload?.custom_cuts || {};
        const customCutsBySide = {
            front: Array.isArray(rawCustomCuts?.front) ? rawCustomCuts.front : [],
            back: Array.isArray(rawCustomCuts?.back) ? rawCustomCuts.back : [],
        };
        const customCutZoneColors = rawCustomCuts?.zone_colors && typeof rawCustomCuts.zone_colors === "object"
            ? rawCustomCuts.zone_colors
            : {};
        const customCutEditor = rawCustomCuts?.editor && typeof rawCustomCuts.editor === "object"
            ? rawCustomCuts.editor
            : {};

        return {
            ok: true,
            draft: {
                currentStep: Number(payload.currentStep) || 1,
                activePartSectionKey: String(payload.activePartSectionKey || ""),
                hasStripeDrawings,
                stripeDrawingsBySide,
                customCutsBySide,
                customCutZoneColors,
                customCutEditor,
                state: {
                    selectedKeys,
                    selectedMaterials,
                    selectedColors,
                    selectedPrints,
                    selectedFullprints,
                    partSublimation,
                    printFilters,
                    generalMaterialId: inputState.generalMaterialId || null,
                    generalSublimation: !!inputState.generalSublimation,
                    generalFullprint: inputState.generalFullprint || null,
                    activePartKey: inputState.activePartKey || null,
                    activePrintKey:
                        inputState.activePrintKey
                        && (
                            String(inputState.activePrintKey).includes("::")
                            || knownSet.has(String(inputState.activePrintKey))
                        )
                            ? inputState.activePrintKey
                            : null,
                    viewSide: inputState.viewSide === "back" ? "back" : "front",
                },
            },
        };
    } catch (error) {
        return { ok: false, reason: "json" };
    }
};
